import { useErrorReporter } from '@/composables/useErrorReporter'
import { useToast } from '@/composables/useToast'
import {
	API_BASE,
	API_ENDPOINT_PORTAL_AUDIT_LOGS,
	API_ENDPOINT_PORTAL_AUDIT_LOGS_APPROVE,
	API_ENDPOINT_PORTAL_AUDIT_LOGS_BY_ID,
	API_ENDPOINT_PORTAL_AUDIT_LOGS_DECLINE
} from '@/constants/index'
import type { AuditLogApiResponse, MappedAuditLog } from '@/types/audit-logs'
import { mapAuditLogs } from '@/utils/mappers/AuditLogsMap'
import { ref } from 'vue'

const loading = ref(false)
const putLoading = ref(false)
const allAuditLogs = ref<MappedAuditLog[]>([])
const auditLogs = ref<MappedAuditLog>()

export function useAuditLogs() {
	const { bugsnagReport } = useErrorReporter()
	const { addToast } = useToast()
	const getAuditLogs = async () => {
		loading.value = true

		await $fetch(`${API_BASE}${API_ENDPOINT_PORTAL_AUDIT_LOGS}`, {
			method: 'GET',
			credentials: 'include',
			onResponse({ response }) {
				if (!response.ok) {
					throw new Error(response._data?.errors?.[0]?.title || 'Error fetching audit logs')
				}
				allAuditLogs.value = response._data?.map((logs: AuditLogApiResponse) => mapAuditLogs(logs)) || []
			}
		}).catch(e => {
			bugsnagReport({
				error: e,
				showToast: true,
				toast: {
					title: 'Error fetching audit logs',
					message: e.message || ''
				}
			})
		}).finally(() => {
			loading.value = false
		})
	}

	const getAuditLogsById = async (id: string) => {
		loading.value = true
		await $fetch(`${API_BASE}${API_ENDPOINT_PORTAL_AUDIT_LOGS_BY_ID.replace('{audit-id}', id)}`, {
			method: 'GET',
			credentials: 'include',
			onResponse({ response }) {
				if (!response.ok) {
					throw new Error(response._data?.errors?.[0]?.title || 'Error fetching audit logs by id')
				}
				auditLogs.value = mapAuditLogs(response._data)
			}
		}).catch(e => {
			bugsnagReport({
				error: e,
				showToast: true,
				toast: {
					title: 'Error fetching audit logs by id',
					message: e.message || ''
				}
			})
		}).finally(() => {
			loading.value = false
		})
	}

	const approveAuditLog = async (id: string, zoneId: string) => {
		putLoading.value = true
		await $fetch<{ success: boolean }>(`${API_BASE}${API_ENDPOINT_PORTAL_AUDIT_LOGS_APPROVE.replace('{audit-id}', id)}`, {
			method: 'PUT',
			credentials: 'include',
			async onResponse({ response }) {
				if (!response.ok) {
					throw new Error(response._data?.errors?.[0]?.title || 'Error approving audit log')
				}
				addToast({
					title: 'Audit log approved',
					notificationType: 'success'
				})
				await navigateTo({
					name: 'internal-zones-zoneid',
					params: { zoneid: zoneId }
				})
			}
		}).catch(e => {
			bugsnagReport({
				error: e,
				showToast: true,
				toast: {
					title: 'Error approving audit log',
					message: e.message || ''
				}
			})
		}).finally(() => {
			putLoading.value = false
		})
	}

	const declineAuditLog = async (id: string, zoneId: string) => {
		putLoading.value = true
		await $fetch<{ success: boolean }>(`${API_BASE}${API_ENDPOINT_PORTAL_AUDIT_LOGS_DECLINE.replace('{audit-id}', id)}`, {
			method: 'PUT',
			credentials: 'include',
			async onResponse({ response }) {
				if (!response.ok) {
					throw new Error(response._data?.errors?.[0]?.title || 'Error declining audit log')
				}
				addToast({
					title: 'Audit log declined',
					notificationType: 'success'
				})
				await navigateTo({
					name: 'internal-zones-zoneid',
					params: { zoneid: zoneId }
				})
			}
		}).catch(e => {
			bugsnagReport({
				error: e,
				showToast: true,
				toast: {
					title: 'Error declining audit log',
					message: e.message || ''
				}
			})
		}).finally(() => {
			putLoading.value = false
		})
	}

	return {
		loading,
		putLoading,
		auditLogs,
		allAuditLogs,
		getAuditLogs,
		getAuditLogsById,
		approveAuditLog,
		declineAuditLog
	}
}
